import { ref, watch, onMounted, onUnmounted } from 'vue';
import { apiClient } from '@/shared/services/api';
import { useAuthStore } from '@/domains/auth/store/authStore';
import { getAccessToken, getTokenExpiration } from '@/shared/utils/tokenHelper';

/**
 * Composable to manage the secure media session cookie.
 * 
 * This composable:
 * 1. Calls the backend to set/refresh the HttpOnly media_token cookie.
 * 2. Sets up an interval to automatically refresh the token before it expires.
 * 3. Monitors auth state to ensure we only request tokens when authenticated.
 * 
 * The media_token is required for accessing /uploads-secure/* paths.
 */
export function useMediaSession() {
    const isReady = ref(false);
    let refreshInterval = null;

    // Configuration
    const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
    // Reduced to 5 minutes to provide a safety buffer against 10-minute expiry
    const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

    /**
     * Check if user has a valid session (token or cookie session)
     */
    function hasValidSession() {
        if (!isUseCookies) {
            // Token-based auth: check if access token exists
            const token = getAccessToken();
            return !!token;
        } else {
            // Cookie-based auth: check if we have a token expiration timestamp
            // (set during successful login - indicates a session was established)
            const expiration = getTokenExpiration();
            return !!expiration;
        }
    }

    /**
     * Initialize or refresh the media session
     */
    async function initMediaSession() {
        const authStore = useAuthStore();

        // Safety check: Don't attempt to refresh if not authenticated
        if (!authStore.isAuthenticated) {
            stopRefresh();
            return;
        }

        // Additional check: Verify we actually have a session
        if (!hasValidSession()) {
            return;
        }

        try {
            // The backend will set the HttpOnly cookie 'media_token' in the response
            await apiClient.post('/auth/media-session');
            isReady.value = true;
        } catch (e) {
            console.error('[MediaSession] Failed to init media session', e);

            // If we get a 401, it means our main session is likely invalid.
            // We should stop trying to refresh the media session.
            if (e?.response?.status === 401) {
                stopRefresh();
            }
        }
    }

    function startRefresh() {
        if (refreshInterval) return; // Already running

        // Verify we have a valid session before starting
        if (!hasValidSession()) {
            return;
        }

        // Initial call
        initMediaSession();

        // Setup auto-refresh
        refreshInterval = setInterval(() => {
            initMediaSession();
        }, REFRESH_INTERVAL_MS);
    }

    function stopRefresh() {
        if (refreshInterval) {
            clearInterval(refreshInterval);
            refreshInterval = null;
        }
        isReady.value = false;
    }

    onMounted(() => {
        const authStore = useAuthStore();

        // Only start if BOTH authenticated AND has a valid session
        // This prevents premature calls when authStore defaults to isAuthenticated=true
        if (authStore.isAuthenticated && hasValidSession()) {
            startRefresh();
        }

        // Watch for auth state changes (Login/Logout)
        watch(() => authStore.isAuthenticated, (isAuthenticated) => {
            if (isAuthenticated && hasValidSession()) {
                startRefresh();
            } else {
                stopRefresh();
            }
        });
    });

    onUnmounted(() => {
        stopRefresh();
    });

    return {
        isReady,
        initMediaSession
    };
}
